export enum AppEnvironment {
  PRODUCTION = 'PRODUCTION',
  STAGING = 'STAGING',
  LOCAL = 'LOCAL',
}

export enum BusinessUnitError {
  BUSINESS_UNIT_NAME_IS_NOT_UNIQUE = 'BUSINESS_UNIT_NAME_IS_NOT_UNIQUE',
}

export enum CompanyError {
  COMPANY_NAME_IS_NOT_UNIQUE = 'COMPANY_NAME_IS_NOT_UNIQUE',
}

export enum DepartmentError {
  DEPARTMENT_NAME_IS_NOT_UNIQUE = 'DEPARTMENT_NAME_IS_NOT_UNIQUE',
}

export enum DivisionError {
  DIVISION_NAME_IS_NOT_UNIQUE = 'DIVISION_NAME_IS_NOT_UNIQUE',
}

export enum EpmMasterVddError {
  EPM_MASTER_VDD_NAME_IS_NOT_UNIQUE = 'EPM_MASTER_VDD_NAME_IS_NOT_UNIQUE',
}

export enum EpmMasterFunctionalityError {
  EPM_MASTER_FUNCTIONALITY_NAME_IS_NOT_UNIQUE = 'EPM_MASTER_FUNCTIONALITY_NAME_IS_NOT_UNIQUE',
}

export enum EpmMasterRoleTypeError {
  EPM_MASTER_ROLE_TYPE_NAME_IS_NOT_UNIQUE = 'EPM_MASTER_ROLE_TYPE_NAME_IS_NOT_UNIQUE',
}

export enum EpmMasterAllocationKeyError {
  EPM_MASTER_ALLOCATION_KEY_NAME_IS_NOT_UNIQUE = 'EPM_MASTER_ALLOCATION_KEY_NAME_IS_NOT_UNIQUE',
}

export enum CompanyEmailDomainError {
  COMPANY_EMAIL_DOMAIN_NAME_IS_NOT_UNIQUE = 'COMPANY_EMAIL_DOMAIN_NAME_IS_NOT_UNIQUE',
}

export enum JobError {
  JOB_NAME_IS_NOT_UNIQUE = 'JOB_NAME_IS_NOT_UNIQUE',
}

export enum LocationError {
  LOCATION_NAME_IS_NOT_UNIQUE = 'LOCATION_NAME_IS_NOT_UNIQUE',
}

export enum ReasonForLeavingError {
  REASON_FOR_LEAVING_NAME_IS_NOT_UNIQUE = 'REASON_FOR_LEAVING_NAME_IS_NOT_UNIQUE',
}

// Team review screen (merge/rename) error codes - see contracts/graphql-schema-delta.md's
// "Errors" section. Matches this file's established convention: each value equals the backend
// AppError's `code` string, which CustomDataFetcherExceptionHandler.kt surfaces as the GraphQL
// error's top-level `message` (not `extensions.code`) - so callers compare against
// `error.graphQLErrors?.[0]?.message`, exactly like DepartmentError is compared in DepartmentForm.tsx.
export enum TeamError {
  TEAM_MERGE_STALE_JOB_HISTORY_IDS = 'TEAM_MERGE_STALE_JOB_HISTORY_IDS',
  TEAM_RENAME_STALE_VALUE = 'TEAM_RENAME_STALE_VALUE',
  // 2026-09-16 (research.md #36/#37/#40, FR-081): also raised by teamValueClusters/teamJobHistories
  // when an ADMIN_PRIVATE caller requests a Country outside their accessible-Countries set - the
  // shared handleTeamPermissionDeniedError helper (teamPermissionError.utils.ts) compares against
  // this to redirect rather than just showing a toast, used by TeamValueClusterList.tsx,
  // TeamNameHistory.tsx, and TeamNamesCountrySelect.tsx.
  TEAM_PERMISSION_DENIED = 'TEAM_PERMISSION_DENIED',
}

export enum ApiError {
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
}

export enum AlertType {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum GeneralType {
  BUSINESS_UNIT = 'Business Unit',
  DIVISION = 'Division',
  COMPANY = 'Company',
  DEPARTMENT = 'Department',
  JOB = 'Job',
  LEVEL = 'Level',
  COMPANY_EMAIL_DOMAIN = 'Email Domain',
  LANGUAGE = 'Language',
  LOCATION = 'Location',
  REASON_FOR_LEAVING = 'Reason for Leaving',
  HUBSPOT_PIPELINE = 'HubSpot Pipeline',
  COUNTRY = 'Country',
  CURRENCY = 'Currency',
  EPM_MASTER_VDD = 'VDD',
  EPM_MASTER_FUNCTIONALITY = 'Functionality',
  EPM_MASTER_ROLE_TYPE = 'Role Type',
  EPM_MASTER_ALLOCATION_KEY = 'Allocation Key',
}

export const GeneralTypeCategory: Record<string, Array<GeneralType>> = {
  General: [
    GeneralType.BUSINESS_UNIT,
    GeneralType.DEPARTMENT,
    GeneralType.JOB,
    GeneralType.LEVEL,
    GeneralType.DIVISION,
    GeneralType.COMPANY,
    GeneralType.COMPANY_EMAIL_DOMAIN,
    GeneralType.LOCATION,
    GeneralType.REASON_FOR_LEAVING,
    GeneralType.LANGUAGE,
  ],
  'General (Readonly)': [GeneralType.HUBSPOT_PIPELINE, GeneralType.COUNTRY, GeneralType.CURRENCY],
  EPM: [
    GeneralType.EPM_MASTER_VDD,
    GeneralType.EPM_MASTER_FUNCTIONALITY,
    GeneralType.EPM_MASTER_ROLE_TYPE,
    GeneralType.EPM_MASTER_ALLOCATION_KEY,
  ],
};

export const AUTH_STATE_KEY = 'AUTH-STATE-KEY';

// sessionStorage key for the web sign-in's PKCE code verifier (one per tab, removed once the callback returns).
export const AUTH_PKCE_VERIFIER_KEY = 'AUTH-PKCE-VERIFIER-KEY';

// localStorage key for the company email of the last successful web sign-in, sent to Google as `login_hint`.
// Kept after sign-out. Removed when a sign-in fails because the account is not a registered employee, when
// "This is a shared computer" is checked, and after a successful sign-in on a shared computer.
export const AUTH_LAST_SIGN_IN_ACCOUNT_KEY = 'AUTH-LAST-SIGN-IN-ACCOUNT-KEY';

// localStorage key for the sign-in page's "This is a shared computer" choice ('true' when checked, absent otherwise).
// While it is set, no sign-in account is remembered or sent as `login_hint`.
export const AUTH_SHARED_COMPUTER_KEY = 'AUTH-SHARED-COMPUTER-KEY';

// Title of every error card on the sign-in and sign-in callback pages.
export const SIGN_IN_ERROR_TITLE = 'Sign in Error';
